import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

// ─── Edit Profile Modal ──────────────────────────────────────────────────────
function EditProfileModal({ coach, onClose, onUpdate }) {
  const cp = Array.isArray(coach.coach_profiles) ? coach.coach_profiles[0] : coach.coach_profiles;
  const [form, setForm] = useState({
    full_name: coach.full_name || '',
    business_name: cp?.business_name || '',
    role: coach.role || 'coach',
    status: cp?.status || 'active'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/coaches/${coach.id}/profile`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      setSuccess('Profile updated.');
      onUpdate();
      setTimeout(onClose, 1200);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <h3 style={{ fontSize: 20 }}>Edit Profile — {coach.full_name}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}
        <form onSubmit={handleSave}>
          <div className="form-group">
            <label className="label">Full Name</label>
            <input className="input" value={form.full_name}
              onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} required />
          </div>
          <div className="form-group">
            <label className="label">Business Name</label>
            <input className="input" value={form.business_name}
              onChange={e => setForm(f => ({ ...f, business_name: e.target.value }))} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="label">Role</label>
              <select className="input" value={form.role}
                onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                <option value="coach">Coach</option>
                <option value="senior_coach">Senior Coach</option>
                <option value="partner">Partner</option>
              </select>
            </div>
            <div className="form-group">
              <label className="label">Status</label>
              <select className="input" value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <button type="submit" className="btn btn-primary"
            style={{ width: '100%', justifyContent: 'center' }} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Coach Manage Panel ─────────────────────────────────────────────────────
function CoachManagePanel({ coach, mentors, onClose, onUpdate }) {
  const cp = Array.isArray(coach.coach_profiles) ? coach.coach_profiles[0] : coach.coach_profiles;
  const isPartnerProfile = coach.role === 'partner';

  // Role-based defaults
  const roleCoachPct = coach.role === 'senior_coach' ? 80 : 75;
  const roleHmaTotal = coach.role === 'senior_coach' ? 20 : 25;

  const [mentorPct, setMentorPct] = useState(Number(cp?.mentor_percentage || 0));
  const [selectedMentorId, setSelectedMentorId] = useState(cp?.mentor_id || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showEditProfile, setShowEditProfile] = useState(false);

  // Fetch fresh split data directly using supabaseAdmin via backend
  useEffect(() => {
    const fetchFresh = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/coaches/${coach.id}/split`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          console.log('Fetched split data:', data);
          setMentorPct(Number(data.mentor_percentage || 0));
          setSelectedMentorId(data.mentor_id || '');
        } else {
          console.error('Split fetch failed:', res.status);
        }
      } catch (err) {
        console.error('Split fetch error:', err);
      }
    };
    fetchFresh();
  }, [coach.id]);

  const hmaPct = roleHmaTotal;
  const hmaCompanyPct = hmaPct - mentorPct;
  const coachPct = roleCoachPct;

  const handleSave = async () => {
    if (mentorPct > hmaPct) {
      setError(`Mentor % cannot exceed HMA total of ${hmaPct}%`);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/coaches/${coach.id}/split`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mentor_id: selectedMentorId || null,
          mentor_percentage: mentorPct,
          hma_company_percentage: hmaCompanyPct,
          hma_percentage: hmaPct
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      setSuccess('Split updated successfully.');
      onUpdate();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div onClick={e => e.stopPropagation()} style={{
          background: 'var(--bg-card)', border: '1px solid var(--border-light)',
          borderRadius: 16, width: '100%', maxWidth: 560,
          maxHeight: '90vh', overflowY: 'auto', padding: 32
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
            <div>
              <h3 style={{ fontSize: 22, marginBottom: 4 }}>{coach.full_name}</h3>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{cp?.business_name || coach.email}</span>
                <span style={{
                  fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 600,
                  background: coach.role === 'partner' ? 'rgba(201,169,110,0.15)' : 'rgba(90,85,80,0.2)',
                  color: coach.role === 'partner' ? 'var(--accent)' : 'var(--text-secondary)'
                }}>
                  {coach.role === 'partner' ? 'Partner' : coach.role === 'senior_coach' ? 'Senior Coach' : 'Coach'}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {!isPartnerProfile && (
                <>
                  <button className="btn btn-secondary btn-sm" onClick={() => setShowEditProfile(true)}>Edit Profile</button>
                  <button className="btn btn-secondary btn-sm" onClick={async () => {
                    if (!window.confirm(`Convert ${coach.full_name} to a client? They will lose coach access.`)) return;
                    try {
                      await supabase.from('profiles').update({ role: 'client' }).eq('id', coach.id);
                      setSuccess(`${coach.full_name} converted to client.`);
                      onUpdate();
                      setTimeout(onClose, 1500);
                    } catch (err) { setError('Could not convert: ' + err.message); }
                  }}>→ Client</button>
                  <button className="btn btn-danger btn-sm" onClick={async () => {
                    if (!window.confirm(`Permanently delete ${coach.full_name}? This cannot be undone.`)) return;
                    try {
                      await supabase.from('coach_profiles').delete().eq('user_id', coach.id);
                      await supabase.from('split_tiers').delete().eq('coach_id', coach.id);
                      await supabase.from('profiles').delete().eq('id', coach.id);
                      onClose(); onUpdate();
                    } catch (err) { setError('Could not delete: ' + err.message); }
                  }}>Delete</button>
                </>
              )}
              {isPartnerProfile && (
                <span style={{ fontSize: 12, color: 'var(--text-muted)', padding: '6px 0' }}>Partner — read only</span>
              )}
              <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 22, cursor: 'pointer' }}>✕</button>
            </div>
          </div>

          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          {!isPartnerProfile && (
            <>
              {/* Revenue Split Visual */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
                  Revenue Split
                </div>

                {/* Visual bar */}
                <div style={{ display: 'flex', height: 40, borderRadius: 8, overflow: 'hidden', marginBottom: 12, gap: 2 }}>
                  <div style={{
                    flex: coachPct, background: 'var(--success)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, fontWeight: 700, color: '#fff'
                  }}>{coachPct}%</div>
                  {mentorPct > 0 && (
                    <div style={{
                      flex: mentorPct, background: 'var(--info)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 700, color: '#fff'
                    }}>{mentorPct}%</div>
                  )}
                  <div style={{
                    flex: hmaCompanyPct, background: 'var(--accent)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, fontWeight: 700, color: '#0a0a0a'
                  }}>{hmaCompanyPct}%</div>
                </div>

                {/* Legend */}
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16, fontSize: 13 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 12, height: 12, borderRadius: 3, background: 'var(--success)', display: 'inline-block' }} />
                    Coach keeps {coachPct}%
                  </span>
                  {mentorPct > 0 && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 12, height: 12, borderRadius: 3, background: 'var(--info)', display: 'inline-block' }} />
                      Mentor gets {mentorPct}%
                    </span>
                  )}
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 12, height: 12, borderRadius: 3, background: 'var(--accent)', display: 'inline-block' }} />
                    HMA company gets {hmaCompanyPct}%
                  </span>
                </div>

                {/* Example on $100 */}
                <div style={{
                  background: 'var(--bg-secondary)', borderRadius: 8,
                  padding: '10px 14px', fontSize: 13, color: 'var(--text-secondary)'
                }}>
                  On $100: Coach <strong style={{ color: 'var(--success)' }}>${coachPct}</strong>
                  {mentorPct > 0 && <> · Mentor <strong style={{ color: 'var(--info)' }}>${mentorPct}</strong></>}
                  · HMA <strong style={{ color: 'var(--accent)' }}>${hmaCompanyPct}</strong>
                </div>
              </div>

              {/* Mentor % input */}
              <div style={{ marginBottom: 20 }}>
                <label className="label">
                  Mentor Commission % <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(carved from HMA's {hmaPct}%)</span>
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <input
                    type="range" min="0" max={hmaPct} step="1"
                    value={mentorPct}
                    onChange={e => setMentorPct(Number(e.target.value))}
                    style={{ flex: 1, accentColor: 'var(--accent)' }}
                  />
                  <div style={{
                    minWidth: 52, textAlign: 'center', padding: '6px 10px',
                    background: 'var(--bg-secondary)', borderRadius: 6,
                    border: '1px solid var(--border)', fontWeight: 600, fontSize: 15
                  }}>
                    {mentorPct}%
                  </div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  0% = all goes to HMA company · {hmaPct}% = all of HMA's share goes to mentor
                </div>
              </div>

              {/* Mentor assignment */}
              <div style={{ marginBottom: 24 }}>
                <label className="label">Assigned Mentor</label>
                <select className="input" value={selectedMentorId}
                  onChange={e => setSelectedMentorId(e.target.value)}>
                  <option value="">No mentor assigned</option>
                  {mentors.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.full_name} ({m.role === 'partner' ? 'Partner' : 'Senior Coach'})
                    </option>
                  ))}
                </select>
              </div>

              <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}
                onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          )}

          {/* Partner view — just show info */}
          {isPartnerProfile && (
            <div style={{ color: 'var(--text-secondary)', fontSize: 14, padding: '20px 0', textAlign: 'center' }}>
              Partner accounts manage their own settings.
            </div>
          )}
        </div>
      </div>

      {showEditProfile && (
        <EditProfileModal
          coach={coach}
          onClose={() => setShowEditProfile(false)}
          onUpdate={() => { onUpdate(); setShowEditProfile(false); }}
        />
      )}
    </>
  );
}

// ─── Main Admin Coaches Page ─────────────────────────────────────────────────
export default function AdminCoaches() {
  const { profile } = useAuth();
  const [coaches, setCoaches] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCoach, setShowAddCoach] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [tab, setTab] = useState('active'); // 'active' | 'pending'
  const [form, setForm] = useState({
    email: '', full_name: '', business_name: '', mentor_id: '',
    hma_percentage: 25, mentor_percentage: 5, hma_company_percentage: 20
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [inviting, setInviting] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      // Fetch each independently so one failure doesn't kill the rest
      const coachesRes = await supabase
        .from('profiles')
        .select('*, coach_profiles!coach_profiles_user_id_fkey(*)')
        .in('role', ['coach', 'senior_coach', 'partner'])
        .order('created_at', { ascending: false });

      const mentorsRes = await supabase
        .from('profiles')
        .select('id, full_name, email, role')
        .in('role', ['partner', 'senior_coach'])
        .order('full_name');

      const invitesRes = await supabase
        .from('pending_invites')
        .select('*, mentor:mentor_id(full_name)')
        .eq('status', 'pending')
        .order('invited_at', { ascending: false });

      const coachesData = coachesRes.data || [];

      if (coachesData.length > 0) {
        // Fetch full coach profiles via backend to bypass RLS
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        const cpRes = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/coach-profiles`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const cpData = cpRes.ok ? await cpRes.json() : [];

        const coachesWithProfiles = coachesData.map(coach => {
          const cp = cpData.find(p => p.user_id === coach.id) || null;
          return { ...coach, coach_profiles: cp };
        });
        setCoaches(coachesWithProfiles);
      } else {
        setCoaches([]);
      }

      setMentors(mentorsRes.data || []);
      setPendingInvites(invitesRes.data || []);
    } catch (err) {
      console.error('fetchAll error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCoach = async (e) => {
    e.preventDefault();
    setError('');
    const { mentor_percentage, hma_company_percentage, hma_percentage } = form;
    if (Math.round((Number(mentor_percentage) + Number(hma_company_percentage)) * 100) !== Math.round(Number(hma_percentage) * 100)) {
      setError(`HMA Company % + Mentor % must equal HMA Total %`);
      return;
    }
    setInviting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/coaches`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create coach');
      setSuccess(`Invite sent to ${form.email}${data.inviteLink ? '' : ' — check their email.'}`);
      if (data.inviteLink) setSuccess(`Coach created! Share this link if email doesn't arrive: ${data.inviteLink}`);
      setShowAddCoach(false);
      setForm({ email: '', full_name: '', business_name: '', mentor_id: '', hma_percentage: 25, mentor_percentage: 5, hma_company_percentage: 20 });
      fetchAll();
    } catch (err) {
      setError(err.message);
    } finally {
      setInviting(false);
    }
  };

  const handleResendInvite = async (invite) => {
    setError('');
    setSuccess('');
    console.log('Resending invite for:', invite.email, 'user_id:', invite.user_id);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not authenticated');
      const url = `${process.env.REACT_APP_API_URL}/api/admin/coaches/${invite.user_id}/resend-invite`;
      console.log('Calling:', url);
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      console.log('Resend response:', res.status, data);
      if (!res.ok) throw new Error(data.error || 'Failed to resend invite');
      setSuccess(`Invite resent to ${invite.email} successfully.`);
      if (data.inviteLink) setSuccess(`Invite resent! Backup link: ${data.inviteLink}`);
    } catch (err) {
      console.error('Resend error:', err);
      setError('Failed to resend: ' + err.message);
    }
  };

  const filtered = coaches.filter(c => {
    const matchSearch = !search ||
      c.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      c.email?.toLowerCase().includes(search.toLowerCase());
    if (!matchSearch) return false;
    if (tab === 'myteam') {
      const cp = Array.isArray(c.coach_profiles) ? c.coach_profiles[0] : c.coach_profiles;
      return cp?.mentor_id === profile?.id;
    }
    return true;
  });

  const filteredPending = pendingInvites.filter(i =>
    !search ||
    i.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    i.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div style={{ color: 'var(--text-secondary)' }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
        <div>
          <h1 style={{ fontSize: 32, marginBottom: 8 }}>Coaches</h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            {coaches.length} active · {pendingInvites.length} pending invite{pendingInvites.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAddCoach(true)}>+ Add Coach</button>
      </div>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={() => setTab('active')}
          className={tab === 'active' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>
          All ({coaches.length})
        </button>
        <button onClick={() => setTab('myteam')}
          className={tab === 'myteam' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>
          My Team ({coaches.filter(c => {
            const cp = Array.isArray(c.coach_profiles) ? c.coach_profiles[0] : c.coach_profiles;
            return cp?.mentor_id === profile?.id;
          }).length})
        </button>
        <button onClick={() => setTab('pending')}
          className={tab === 'pending' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>
          Pending ({pendingInvites.length})
        </button>
        <input className="input" placeholder="Search..." value={search}
          onChange={e => setSearch(e.target.value)} style={{ maxWidth: 220, marginLeft: 'auto' }} />
      </div>

      {/* Coaches table — active and myteam */}
      {(tab === 'active' || tab === 'myteam') && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)' }}>
              {search ? 'No coaches match.' : 'No active coaches yet.'}
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Coach</th>
                  <th>Business</th>
                  <th>Level</th>
                  <th>HMA %</th>
                  <th>Coach Keeps</th>
                  <th>Mentor</th>
                  <th>Stripe</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => {
                  const cp = Array.isArray(c.coach_profiles) ? c.coach_profiles[0] : c.coach_profiles;
                  const tiers = cp?.split_tiers || [];
                  const activeTier = tiers.find(t => t.is_active);
                  const hmaP = cp?.hma_percentage || 25;
                  const assignedMentor = mentors.find(m => m.id === cp?.mentor_id);
                  return (
                    <tr key={c.id}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{c.full_name || '—'}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.email}</div>
                      </td>
                      <td style={{ color: 'var(--text-secondary)' }}>{cp?.business_name || '—'}</td>
                      <td>
                        <span style={{
                          background: c.role === 'partner' ? 'rgba(201,169,110,0.15)' : c.role === 'senior_coach' ? 'rgba(85,153,221,0.12)' : 'rgba(90,85,80,0.15)',
                          color: c.role === 'partner' ? 'var(--accent)' : c.role === 'senior_coach' ? 'var(--info)' : 'var(--text-secondary)',
                          padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500
                        }}>
                          {c.role === 'partner' ? 'Partner' : c.role === 'senior_coach' ? 'Senior Coach' : 'Coach'}
                        </span>
                      </td>
                      <td style={{ color: 'var(--accent)', fontWeight: 500 }}>{hmaP}%</td>
                      <td style={{ color: 'var(--success)', fontWeight: 500 }}>{100 - hmaP}%</td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                        {assignedMentor?.full_name || <span style={{ color: 'var(--text-muted)' }}>None</span>}
                      </td>
                      <td>
                        <span className={`badge ${cp?.stripe_onboarding_complete ? 'badge-active' : 'badge-pending'}`}>
                          {cp?.stripe_onboarding_complete ? 'Connected' : 'Pending'}
                        </span>
                      </td>
                      <td>
                        <button className="btn btn-secondary btn-sm" onClick={() => setSelectedCoach(c)}>
                          Manage
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Pending invites table */}
      {tab === 'pending' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {filteredPending.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)' }}>
              No pending invites.
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mentor</th>
                  <th>HMA Split</th>
                  <th>Invited</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPending.map(invite => (
                  <tr key={invite.id}>
                    <td style={{ fontWeight: 500 }}>{invite.full_name || '—'}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{invite.email}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                      {invite.mentor?.full_name || <span style={{ color: 'var(--text-muted)' }}>None</span>}
                    </td>
                    <td style={{ color: 'var(--accent)' }}>{invite.hma_percentage}%</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                      {new Date(invite.invited_at).toLocaleDateString('en-CA')}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <span style={{ fontSize: 11, background: 'rgba(240,165,0,0.15)', color: 'var(--warning)', padding: '3px 10px', borderRadius: 20 }}>
                          Pending
                        </span>
                        <button className="btn btn-secondary btn-sm"
                          onClick={() => handleResendInvite(invite)}>
                          Resend
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Coach tier panel */}
      {selectedCoach && (
        <CoachManagePanel
          coach={selectedCoach}
          mentors={mentors}
          onClose={() => setSelectedCoach(null)}
          onUpdate={fetchAll}
        />
      )}

      {/* Add Coach Modal */}
      {showAddCoach && (
        <div className="modal-overlay" onClick={() => setShowAddCoach(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ fontSize: 20 }}>Add New Coach</h3>
              <button onClick={() => setShowAddCoach(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 20, cursor: 'pointer' }}>✕</button>
            </div>
            {error && <div className="alert alert-error">{error}</div>}
            <form onSubmit={handleCreateCoach}>
              <div className="form-group">
                <label className="label">Full Name</label>
                <input className="input" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="label">Email</label>
                <input type="email" className="input" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="label">Business Name</label>
                <input className="input" value={form.business_name} onChange={e => setForm(f => ({ ...f, business_name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="label">Assign Mentor</label>
                <select className="input" value={form.mentor_id} onChange={e => setForm(f => ({ ...f, mentor_id: e.target.value }))}>
                  <option value="">None</option>
                  {mentors.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.full_name} {m.role === 'partner' ? '(Partner)' : m.role === 'senior_coach' ? '(Senior Coach)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="label">HMA Total %</label>
                  <input type="number" step="0.5" className="input" value={form.hma_percentage}
                    onChange={e => setForm(f => ({ ...f, hma_percentage: Number(e.target.value) }))} />
                </div>
                <div className="form-group">
                  <label className="label">HMA Co %</label>
                  <input type="number" step="0.5" className="input" value={form.hma_company_percentage}
                    onChange={e => setForm(f => ({ ...f, hma_company_percentage: Number(e.target.value) }))} />
                </div>
                <div className="form-group">
                  <label className="label">Mentor %</label>
                  <input type="number" step="0.5" className="input" value={form.mentor_percentage}
                    onChange={e => setForm(f => ({ ...f, mentor_percentage: Number(e.target.value) }))} />
                </div>
              </div>
              <div style={{
                background: 'rgba(201,169,110,0.06)', border: '1px solid rgba(201,169,110,0.15)',
                borderRadius: 8, padding: '10px 14px', marginBottom: 20,
                fontSize: 13, color: 'var(--text-secondary)'
              }}>
                On $100: Coach keeps <strong style={{ color: 'var(--success)' }}>${100 - form.hma_percentage}</strong> ·
                HMA co <strong style={{ color: 'var(--accent)' }}>${form.hma_company_percentage}</strong> ·
                Mentor <strong style={{ color: 'var(--info)' }}>${form.mentor_percentage}</strong>
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={inviting}>
                {inviting ? 'Sending Invite...' : 'Create Coach & Send Invite'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
